/**
 * RetiredAppPage.jsx — tells a signed-in user a product was retired and what to open next.
 * Shaped by: Prompt 21; owner decisions 2026-10-08.
 * Invariants: one filled button per state; an access error shows Retry and never the replacement;
 *   no PHI; this page does not grant access.
 * Related: RetiredChartMindRoute.jsx, useClinicalHudAccess.js.
 */
import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useNavigate } from "react-router-dom";

export default function RetiredAppPage({
  appName,
  replacementName,
  replacementPath,
  hasReplacementAccess,
  accessLoading,
  accessError,
  onRetry,
}) {
  const navigate = useNavigate();
  const title = `${appName} has been retired`;

  if (accessLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (accessError) {
    return (
      <Stack spacing={2} sx={{ p: 3, maxWidth: 480 }}>
        <Typography variant="h5">{title}</Typography>
        <Typography color="text.secondary">
          We could not check access. Nothing was opened.
        </Typography>
        <Button variant="contained" onClick={onRetry} data-testid="chartmind.retired.retry">
          Retry
        </Button>
      </Stack>
    );
  }

  if (hasReplacementAccess) {
    return (
      <Stack spacing={2} sx={{ p: 3, maxWidth: 480 }}>
        <Typography variant="h5">{title}</Typography>
        <Typography color="text.secondary">
          {`Use ${replacementName} for encounter documentation.`}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            onClick={() => navigate(replacementPath)}
            data-testid="chartmind.retired.openReplacement"
          >
            {`Open ${replacementName}`}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate("/dashboard")}
            data-testid="chartmind.retired.backToApps"
          >
            Back to Apps
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ p: 3, maxWidth: 480 }}>
      <Typography variant="h5">{title}</Typography>
      <Typography color="text.secondary">
        {`Ask your administrator for ${replacementName} access`}
      </Typography>
      <Button
        variant="contained"
        onClick={() => navigate("/dashboard")}
        data-testid="chartmind.retired.backToApps"
      >
        Back to Apps
      </Button>
    </Stack>
  );
}
